import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useSyncDescriptionGroups } from "./hooks/useSyncDescriptionGroups";

import DescriptionGroupsForm from "@/components/forms/client/DescriptionGroupsForm";
import GroupClientDescriptionsForm from "@/components/forms/client/GroupClientDescriptionsForm";
import ModernPageLayout from "@/components/layout/ModernPageLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/v2/alert-dialog";
import { Button } from "@/components/ui/v2/button";
import { ROUTES } from "@/constants/routes";
import { ClientProvider } from "@/lib/contexts/Client/ClientProvider";
import { DescriptionGroupProvider } from "@/lib/contexts/DescriptionGroup/DescriptionGroupProvider";
import { useAuth } from "@/lib/hooks/useAuth";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { useClients } from "@/lib/hooks/useClients";
import useToast from "@/lib/hooks/useToast";

type ClientDescriptionsPageContentProps = {
  clientId: string;
  tenantId: string | null;
};

const ClientDescriptionsPageContent = ({
  clientId,
  tenantId,
}: ClientDescriptionsPageContentProps) => {
  const { t } = useTranslation();
  const { client } = useClients();
  const { showToast } = useToast();
  const syncMutation = useSyncDescriptionGroups();
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.clients"), href: `/${ROUTES.clients}` },
      {
        label: client?.name || t("common.client"),
        href: `/${ROUTES.clients}/${clientId}`,
      },
      { label: t("common.descriptions") },
    ],
  });

  const handleSyncConfirm = () => {
    syncMutation.mutate(
      { clientId },
      {
        onSuccess: (data) => {
          showToast(data.message, "success");
        },
        onError: () => {
          showToast(t("common.error"), "error");
        },
      }
    );
    setShowSyncConfirm(false);
  };

  return (
    <DescriptionGroupProvider clientId={clientId} name="" tenantId={tenantId}>
      <ModernPageLayout
        breadcrumbs={breadcrumbs}
        title={t("common.descriptions")}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSyncConfirm(true)}
            disabled={syncMutation.isPending}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${syncMutation.isPending ? "animate-spin" : ""}`}
            />
            {syncMutation.isPending
              ? t("common.syncing")
              : t("common.syncPublicationChannels")}
          </Button>
        }
      >
        <DescriptionGroupsForm clientId={clientId} tenantId={tenantId} />
        <GroupClientDescriptionsForm clientId={clientId} tenantId={tenantId} />
      </ModernPageLayout>

      <AlertDialog open={showSyncConfirm} onOpenChange={setShowSyncConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("common.syncPublicationChannels")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.client.descriptions.syncConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSyncConfirm}
              disabled={syncMutation.isPending}
            >
              {t("common.sync")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DescriptionGroupProvider>
  );
};

const ClientDescriptionsPage = () => {
  const { clientId } = useParams();
  const { tenantId } = useAuth();

  if (!clientId) {
    return null;
  }

  return (
    <ClientProvider clientId={clientId} tenantId={tenantId}>
      <ClientDescriptionsPageContent clientId={clientId} tenantId={tenantId} />
    </ClientProvider>
  );
};

export default ClientDescriptionsPage;
